import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import { Star, Edit, Trash2, Eye, Plus, User } from 'lucide-react';
import PageHeader from '../../components/admin/PageHeader';
import DataTable from '../../components/admin/DataTable';
import LoadingState from '../../components/admin/LoadingState';
import ConfirmDialog from '../../components/admin/ConfirmDialog';
import toast from 'react-hot-toast';

const Testimonials: React.FC = () => {
  const [testimonials, setTestimonials] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [deleteConfirm, setDeleteConfirm] = useState<{isOpen: boolean, testimonial: any}>({
    isOpen: false,
    testimonial: null
  });
  const navigate = useNavigate();

  useEffect(() => {
    document.title = 'Gestion des Témoignages | Admin';
    loadTestimonials();
  }, []);

  const loadTestimonials = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('testimonials')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;
      setTestimonials(data || []);
    } catch (error) {
      console.error('Error loading testimonials:', error);
      toast.error('Erreur lors du chargement des témoignages');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (testimonial: any) => {
    navigate(`/admin/testimonials/edit/${testimonial.id}`);
  };

  const handleView = (testimonial: any) => {
    // Just show a preview toast for now
    toast.success(
      <div>
        <p className="font-medium">{testimonial.author}</p>
        <p className="text-sm">{testimonial.content.substring(0, 100)}...</p>
      </div>,
      { duration: 5000 }
    );
  };

  const handleDelete = async () => {
    if (!deleteConfirm.testimonial) return;
    
    try {
      const { error } = await supabase
        .from('testimonials')
        .delete()
        .eq('id', deleteConfirm.testimonial.id);

      if (error) throw error;
      
      setTestimonials(testimonials.filter(t => t.id !== deleteConfirm.testimonial.id));
      toast.success('Témoignage supprimé avec succès');
    } catch (error) {
      console.error('Error deleting testimonial:', error);
      toast.error('Erreur lors de la suppression du témoignage');
    } finally {
      setDeleteConfirm({ isOpen: false, testimonial: null });
    }
  };

  const columns = [
    {
      key: 'author',
      header: 'Auteur',
      render: (value: string, item: any) => (
        <div className="flex items-center">
          <div className="w-10 h-10 flex-shrink-0 mr-3">
            {item.image ? (
              <img 
                src={item.image} 
                alt={value}
                className="w-10 h-10 rounded-full object-cover"
                onError={(e) => {
                  (e.target as HTMLImageElement).src = 'https://via.placeholder.com/40';
                }}
              />
            ) : (
              <div className="w-10 h-10 rounded-full bg-yellow-100 flex items-center justify-center">
                <User size={20} className="text-yellow-600" />
              </div>
            )}
          </div>
          <div>
            <div className="font-medium text-gray-900">{value}</div>
            <div className="text-xs text-gray-500">
              {item.position && item.company 
                ? `${item.position}, ${item.company}`
                : item.position || item.company || ''}
            </div>
          </div>
        </div>
      ),
      sortable: true
    },
    {
      key: 'content',
      header: 'Témoignage',
      render: (value: string) => (
        <div className="max-w-xs truncate">{value}</div>
      )
    },
    {
      key: 'rating',
      header: 'Note',
      render: (value: number) => (
        <div className="flex">
          {[...Array(5)].map((_, i) => (
            <Star 
              key={i} 
              size={16} 
              className={i < value ? "text-yellow-400 fill-current" : "text-gray-300"} 
            />
          ))}
        </div>
      ),
      sortable: true,
      width: 'w-32'
    },
    {
      key: 'created_at',
      header: 'Ajouté le',
      render: (value: string) => new Date(value).toLocaleDateString('fr-FR'),
      sortable: true,
      width: 'w-32'
    }
  ];

  return (
    <div className="px-6">
      <PageHeader
        title="Témoignages"
        description="Gérez les témoignages de vos clients"
        actionLink="/admin/testimonials/new"
        actionLabel="Ajouter un témoignage"
        actionIcon={<Plus size={16} />}
      />
      
      {loading ? (
        <LoadingState type="table" />
      ) : (
        <DataTable
          columns={columns}
          data={testimonials}
          onEdit={handleEdit}
          onDelete={(testimonial) => setDeleteConfirm({ isOpen: true, testimonial })}
          onView={handleView}
          searchPlaceholder="Rechercher un témoignage..."
          searchKey="author"
          emptyTitle="Aucun témoignage"
          emptyDescription="Vous n'avez pas encore ajouté de témoignage."
          emptyActionLabel="Ajouter un témoignage"
          onEmptyAction={() => navigate('/admin/testimonials/new')}
        />
      )}
      
      <ConfirmDialog
        isOpen={deleteConfirm.isOpen}
        title="Supprimer le témoignage"
        message={`Êtes-vous sûr de vouloir supprimer le témoignage de "${deleteConfirm.testimonial?.author}" ? Cette action est irréversible.`}
        confirmLabel="Supprimer"
        cancelLabel="Annuler"
        onConfirm={handleDelete}
        onCancel={() => setDeleteConfirm({ isOpen: false, testimonial: null })}
        type="danger"
      />
    </div>
  );
};

export default Testimonials;